<template>
  <aside class="sidebar">
    <!-- Search Section -->
    <div class="sidebar-section">
      <div class="sidebar-content py-3">
        <div class="search-input-wrapper">
          <b-icon icon="search" class="search-icon"></b-icon>
          <input
            type="text"
            class="search-input"
            :placeholder="$t('sidebar.searchPlaceholder')"
            :value="searchQuery"
            @input="onSearchInput"
          />
          <button
            v-if="searchQuery"
            class="search-clear"
            @click="clearSearch"
          >
            <b-icon icon="x"></b-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- Calendar Section -->
    <div class="sidebar-section">
      <div class="sidebar-header" @click="toggleCalendar">
        <span class="sidebar-toggle">
          <b-icon :icon="calendarOpen ? 'chevron-up' : 'chevron-down'"></b-icon>
        </span>
      </div>
      <div class="sidebar-content" v-show="calendarOpen">
        <Calendar
          :event-dates="eventDates"
          @date-selected="onDateSelected"
        />
      </div>
    </div>

    <!-- Display Toggle -->
    <div class="sidebar-section">
      <div class="sidebar-content py-3">
        <div class="display-toggle">
          <span class="display-label">{{ $t('sidebar.display') }}</span>
          <button
            class="toggle-btn"
            :class="{ 'active': displayMode === 'grid' }"
            @click="setDisplayMode('grid')"
          >
            <b-icon icon="grid-3x3-gap"></b-icon>
          </button>
          <button
            class="toggle-btn"
            :class="{ 'active': displayMode === 'list' }"
            @click="setDisplayMode('list')"
          >
            <b-icon icon="list"></b-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="sidebar-section">
      <div class="sidebar-header" @click="toggleFilters">
        <h3 class="sidebar-title">
          <b-icon icon="sliders"></b-icon>
          {{ $t('sidebar.filters') }}
        </h3>
        <span class="sidebar-toggle">
          <b-icon :icon="filtersOpen ? 'chevron-up' : 'chevron-down'"></b-icon>
        </span>
      </div>
      <div class="sidebar-content" v-show="filtersOpen">
        <StoreFilter v-if="!$store.state.placeCode" @filter-changed="onFilterChanged" />
        <ThemeFilter
          :available-themes="availableThemes"
          @filter-changed="onFilterChanged"
        />
        <PriceFilter @filter-changed="onFilterChanged" />
      </div>
    </div>
  </aside>
</template>

<script>
import Calendar from './Calendar.vue'
import StoreFilter from './StoreFilter.vue'
import ThemeFilter from './ThemeFilter.vue'
import PriceFilter from './PriceFilter.vue'

export default {
  name: 'Sidebar',

  components: {
    Calendar,
    StoreFilter,
    ThemeFilter,
    PriceFilter
  },

  props: {
    eventDates: {
      type: Array,
      default: () => []
    },
    availableThemes: {
      type: Array,
      default: () => []
    }
  },

  data() {
    const isMobile = window.innerWidth <= 992
    return {
      calendarOpen: !isMobile,
      filtersOpen: !isMobile,
      displayMode: 'grid',
      isMobile
    }
  },

  computed: {
    searchQuery() {
      return this.$store.state.searchQuery
    }
  },

  mounted() {
    window.addEventListener('resize', this.handleResize)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },

  methods: {
    handleResize() {
      this.isMobile = window.innerWidth <= 992
    },

    onSearchInput(event) {
      this.$store.dispatch('setSearchQuery', event.target.value)
      this.$emit('search-changed', event.target.value)
    },

    clearSearch() {
      this.$store.dispatch('setSearchQuery', '')
      this.$emit('search-changed', '')
    },

    toggleCalendar() {
      this.calendarOpen = !this.calendarOpen
    },

    toggleFilters() {
      this.filtersOpen = !this.filtersOpen
    },

    setDisplayMode(mode) {
      this.displayMode = mode
      this.$emit('display-mode-changed', mode)
    },

    onDateSelected(date) {
      this.$store.dispatch('setSelectedDate', date)
      this.$emit('date-selected', date)
    },

    onFilterChanged() {
      this.$emit('filter-changed')
    }
  }
}
</script>

<style lang="scss" scoped>
.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;

  .search-icon {
    position: absolute;
    left: 12px;
    color: #999;
    pointer-events: none;
  }

  .search-input {
    width: 100%;
    padding: 10px 36px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.9rem;
    transition: border-color 0.2s;

    &:focus {
      outline: none;
      border-color: #7B7B3F;
    }

    &::placeholder {
      color: #999;
    }
  }

  .search-clear {
    position: absolute;
    right: 8px;
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: #999;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: #333;
    }
  }
}
</style>
